import { useEffect, useMemo } from "react";
// import axios from 'axios';
import { addWeeks, format, startOfWeek } from "date-fns";
import _ from "lodash";

import {
	BuildingOfficeIcon,
	CalculatorIcon,
	CheckBadgeIcon,
	CheckIcon,
	ClockIcon,
	ExclamationCircleIcon,
} from "@heroicons/react/20/solid";
import { BanknotesIcon, ChevronRightIcon, PencilIcon } from "@/icons/outline";
import { connect } from "react-redux";
import { Actions as ALLOWANCE_ACTIONS } from "../../store/actions/allowanceActions";
import { Actions as LEAVE_ACTIONS } from "../../store/actions/leaveActions";
import { Actions as OVERTIME_ACTIONS } from "../../store/actions/overtimeActions";
import { Actions as PAYROLL_ACTIONS } from "../../store/actions/payrollActions";

import Tooltip from "@/tooltip";
import Spinner from "../common/Spinner";
import PayslipNote from "./PayslipNote";
import SalaryRow from "./SalaryRow";
import TaxRow from "./TaxRow";

import { v4 as uuidv4 } from "uuid";
import { EyeIcon, PencilSquareIcon } from "@/icons/solid";

const EmployeeRow = ({
	employee,
	jurisdiction,
	rowNo,
	employeeID,
	payPeriodDetails,
	payrollToggleEmployeeExpand,
	payrollSaveAllowances,
	extraEngagementWeeks,
	weeksBeforeTermination,
	weeksAfterPeriodEnd,
	toggleTaxableWeek,
	payrollUpdateAllowance,
	unprocessTimesheetsModal,
	leaveBalance,
	proRataLeave,
	previewPayslip,
	engagementDate,
	terminationDate,
	salaryUpdatedAfterPayslip,
	currency,
	getSingularBalance,
	userEntersData,
	...restprops
}) => {
	// auto-toggle taxable weeks
	useEffect(() => {
		employee?.taxableWeeks?.forEach?.((week) => {
			if (weeksBeforeTermination && !weeksBeforeTermination.includes(week)) {
				//switch off week as out of range?
				toggleTaxableWeek({ week, employeeID });
			}
		});
	}, []);

	const {
		id,
		fullName,
		basePay,
		grossPay,
		allowances,
		taxes,
		payslip,
		expand,
		generating,
		error,
		salaries,
		employmentTypeName,
	} = employee;
	const {
		salaryAmount,
		payPeriodFrequencyName: salaryFrequency,
		salaryAllowances,
		salaryOvertime,
		casual,
		weeklyHours,
	} = salaries[salaries.length - 1];

	const taxesToDisplay = useMemo(() => {
		if (taxes?.length > 0) return taxes;
		else
			return (
				taxes?.map(({ taxVariable, ...item }) => {
					return {
						employeeCalculation: item.calculation,
						taxName: taxVariable?.taxName,
					};
				}) || []
			);
	}, [JSON.stringify(taxes)]);

	const JoinerLeaverNotice = () => {
		return (
			<>
				{engagementDate >= payPeriodDetails.periodStart && (
					<a
						className="btn btn-xs btn-success"
						style={{ marginLeft: 5 }}
						type="button"
						data-toggle="tooltip"
						title="This is a new hire"
					>
						Engaged {format(new Date(engagementDate), "d MMM")}
					</a>
				)}

				{terminationDate <= payPeriodDetails.periodEnd && (
					<a
						className="btn btn-xs btn-danger"
						style={{ marginLeft: 5 }}
						type="button"
						data-toggle="tooltip"
						title="This employee has been terminated this period"
					>
						Terminated {format(new Date(terminationDate), "d MMM")}
					</a>
				)}

				{payslip?.toAdvisor && !ready && (
					<Tooltip
						content={`From Client: ${payslip?.toAdvisor}`}
						as="span"
						className="mr-3"
						style={{ marginLeft: 5 }}
					>
						<BuildingOfficeIcon
							className="text-primary"
							style={{ height: "2rem", verticalAlign: "middle" }}
						/>
					</Tooltip>
				)}
			</>
		);
	};

	const ready = ["yes", "true"].includes(String(payslip?.ready));
	const saved = payslip?.dataSaved;
	const cannotEdit = ready || generating || (userEntersData ? saved : false);
	const processedTimesheets = ["yes", "true"].includes(String(payslip?.processedTimesheets));

	const changedEmploymentTypeMidMonth = _.uniqBy(salaries, "employmentType").length > 1;

	const terminated =
		terminationDate && terminationDate != engagementDate && terminationDate < payPeriodDetails.periodEnd;
	const engaged = engagementDate && engagementDate > payPeriodDetails.periodStart;

	const hasPaidLeave = _.find(employee.allowances, { allowanceType: 75668 });

	const TotalsBlock = () => {
		return (
			<div
				style={{
					display: "grid",
					gridGap: "1.5rem",
					grid: "1.5rem",
					gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
				}}
			>
				<div style={{ gridColumn: "span 6 / span 6", borderTop: "1px solid #bbbcbc" }}></div>
				<div style={{ gridColumn: "span 2 / span 6" }}>
					<strong>Gross</strong>
				</div>
				<div style={{ gridColumn: "span 2 / span 6" }}></div>
				<div style={{ gridColumn: "span 2 / span 6" }}>
					{payslip?.gross && parseFloat(payslip?.gross)?.toFixed(2)}
				</div>

				<div style={{ gridColumn: "span 2 / span 6" }}>
					<strong>Net Pay</strong>
				</div>
				<div style={{ gridColumn: "span 2 / span 6" }}></div>
				<div style={{ gridColumn: "span 2 / span 6" }}>
					{payslip?.net && parseFloat(payslip?.net)?.toFixed(2)}
				</div>
			</div>
		);
	};

	return (
		<div
			className="employee-payroll-card"
			style={{
				padding: "0px 15px 0 45px",
				backgroundColor: rowNo % 2 ? "#ea611c0a" : "none",
				position: "relative",
			}}
		>
			<div
				style={{
					cursor: "pointer",
					position: "absolute",
					left: 10,
					width: 38,
					height: 38,
					lineHeight: "38px",
					textAlign: "center",
					transform: expand ? "rotate(90deg)" : "rotate(0deg)",
					transition: "transform 0.2s ease-in-out",
				}}
				onClick={() => {
					payrollToggleEmployeeExpand(id);
					if (!payslip?.leaveBalance && !expand)
						getSingularBalance(
							id,
							payPeriodDetails.leaveCutOff ? payPeriodDetails.leaveCutOff : payPeriodDetails.periodEnd,
						);
				}}
			>
				<ChevronRightIcon className="h-5 w-5" />
			</div>
			<div
				style={{
					display: "grid",
					gridGap: "1.5rem",
					grid: "1.5rem",
					gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
					lineHeight: "38px",
				}}
			>
				<div
					style={{ gridColumn: "span 2 / span 12" }}
					className="font-w600"
				>
					{fullName}
					<Tooltip
						content="Click to Edit Employee"
						as="span"
						style={{ marginLeft: 5 }}
					>
						<a
							className="text-default"
							type="button"
							target="_blank"
							href={`/organisation/people/${employee.id}/`}
						>
							<PencilSquareIcon
								className="h-6 w-6"
								style={{ verticalAlign: "text-top" }}
							/>
						</a>
					</Tooltip>
				</div>
				<div style={{ gridColumn: "span 2 / span 6" }}>
					{changedEmploymentTypeMidMonth
						? "Varied"
						: `${employmentTypeName} ${weeklyHours && weeklyHours != 40 ? `(${weeklyHours}hrs)` : ``}`}
				</div>

				<div style={{ gridColumn: "span 2 / span 6" }}>
					{salaries && salaries.length == 1
						? `${currency}${salaryAmount} / ${salaryFrequency}`
						: `Mixed Salaries`}
				</div>

				{/*<span>{currency}<span className="base-pay">{basePay}</span></span>*/}
				<div style={{ gridColumn: "span 2 / span 6" }}>
					<span className="font-w600">
						{currency}
						<span className="gross-pay">{(parseFloat(payslip?.gross) || 0)?.toFixed(2)}</span>
					</span>
				</div>

				{!userEntersData ? (
					<div style={{ gridColumn: "span 4 / span 6" }}>
						{ready && !generating && !salaryUpdatedAfterPayslip && (
							<>
								<Tooltip
									content={
										salaryUpdatedAfterPayslip
											? "Salary has been updated after this payslip was generated"
											: "Payslip saved successfully"
									}
									as="span"
									className="mr-3"
								>
									{salaryUpdatedAfterPayslip ? (
										<ExclamationCircleIcon
											className="text-danger"
											style={{ width: "2rem", height: "2rem", verticalAlign: "middle" }}
										/>
									) : (
										<CheckIcon
											className="text-success"
											style={{ height: "2rem", verticalAlign: "text-top" }}
										/>
									)}
								</Tooltip>

								<button
									className="btn btn-xs btn-default mr-3 inline-block"
									type="button"
									onClick={() => previewPayslip(employee.payslip.id)}
								>
									<Tooltip
										content="Download Draft Payslip"
										as="span"
										distance={8}
									>
										<EyeIcon
											className="h-6 w-6"
											style={{ verticalAlign: "text-top", marginRight: 2 }}
										/>
										PREVIEW
									</Tooltip>
								</button>
							</>
						)}

						{error && !generating && (
							<Tooltip
								content="There was an error with your payslip. Check that the net amount is not lower than 0."
								as="span"
								className="mr-3"
							>
								<ExclamationCircleIcon
									className="text-danger"
									style={{ height: "2rem", verticalAlign: "middle" }}
								/>
							</Tooltip>
						)}

						{generating && (
							<Spinner
								style={{ width: "2rem", height: "2rem", verticalAlign: "middle" }}
								className="animate-spin -ml-1 mr-3 h-5 w-5 text-success"
							/>
						)}

						{!generating && (
							<button
								onClick={() => {
									if (!generating) {
										payrollSaveAllowances(id, !ready);
									}
								}}
								className={"btn btn-xs btn-default"}
								type="button"
								data-toggle="tooltip"
								title={ready ? "Unlock Payslip" : "Save Payslip"}
								style={{
									display: "inline-block",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								{ready && (
									<>
										<PencilIcon
											className="h-6 w-6"
											style={{ verticalAlign: "text-top", marginRight: 2 }}
										/>
										EDIT
									</>
								)}
								{!ready && !generating && (
									<>
										<CalculatorIcon
											className="h-6 w-6"
											style={{ verticalAlign: "text-top", marginRight: 2 }}
										/>
										CALCULATE & SAVE
									</>
								)}
							</button>
						)}

						{/* If we have processed timesheets show a clock */}
						{processedTimesheets && (
							<span
								style={{ cursor: "pointer" }}
								onClick={() => {
									unprocessTimesheetsModal(employeeID);
								}}
								title="Unprocess Timesheets"
							>
								<ClockIcon
									className="h-7 w-7 ml-3"
									style={{
										display: "inline",
										verticalAlign: "text-top",
									}}
								/>
							</span>
						)}

						<JoinerLeaverNotice />
						{/*<a className="btn btn-xs btn-danger casual-end-date-alert hide" type="button" data-toggle="tooltip" title="Please fill in the period end date for casual workers">
                Period End Date?
                </a>*/}
					</div>
				) : (
					<div style={{ gridColumn: "span 4 / span 6" }}>
						{generating && (
							<Spinner
								style={{ width: "2rem", height: "2rem", verticalAlign: "middle" }}
								className="animate-spin -ml-1 mr-3 h-5 w-5 text-success"
							/>
						)}

						{ready && (
							<Tooltip
								className={`btn btn-xs btn-default`}
								type="button"
								data-toggle="tooltip"
								content="Payslip has been processed by your advisor"
								style={{
									display: "inline-block",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<>
									<CheckBadgeIcon
										className="h-6 w-6"
										style={{ verticalAlign: "text-top", marginRight: 2 }}
									/>
									PROCESSED
								</>
							</Tooltip>
						)}

						{!generating && !ready && (
							<button
								onClick={() => {
									if (!generating) {
										payrollSaveAllowances(id, ready, !saved);
									}
								}}
								className={`btn btn-xs btn-${saved ? "default" : "success"}`}
								type="button"
								data-toggle="tooltip"
								title={`${saved ? `Edit` : "Save"} ${
									jurisdiction === "UK" ? "Pay Items" : "Allowances"
								}`}
								style={{
									display: "inline-block",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								{saved && (
									<>
										<PencilIcon
											className="h-6 w-6"
											style={{ verticalAlign: "text-top", marginRight: 2 }}
										/>
										EDIT
									</>
								)}
								{!saved && !generating && (
									<>
										<CheckBadgeIcon
											className="h-6 w-6"
											style={{ verticalAlign: "text-top", marginRight: 2 }}
										/>
										SAVE
									</>
								)}
							</button>
						)}
						<JoinerLeaverNotice />
					</div>
				)}
			</div>

			{expand && (
				<div
					style={{
						display: "grid",
						gridGap: "1.5rem",
						grid: "1.5rem",
						gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
						gridTemplateRows: userEntersData ? "1fr auto" : "1fr auto 1fr",
					}}
				>
					<div
						style={{
							gridColumnStart: "1",
							gridColumnEnd: "8",
							gridRowStart: "1",
							gridRowEnd: "3",
						}}
					>
						{salaries?.map((salary, index) => (
							<SalaryRow
								key={salary.id}
								salary={salary}
								index={index}
								employeeID={id}
								ready={cannotEdit}
								processedTimesheets={processedTimesheets}
								terminated={terminated}
								engaged={engaged}
							/>
						))}
					</div>

					{!userEntersData && (
						<div
							style={{
								gridColumnStart: "1",
								gridColumnEnd: "8",
								gridRowStart: "3",
								gridRowEnd: "3",
								height: "min-content",
							}}
						>
							<div
								style={{
									display: "grid",
									gridGap: "1.5rem",
									grid: "1.5rem",
									gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
									paddingLeft: 5,
								}}
							>
								<div style={{ gridColumn: "span 6 / span 6", borderTop: "1px solid #bbbcbc" }}></div>
								<div style={{ gridColumnStart: "1", gridColumnEnd: "4" }}>
									Weeks Worked
									<br />
									<div>
										{extraEngagementWeeks?.map((week) => (
											<input
												key={week}
												disabled={ready}
												checked={employee.taxableWeeks?.includes(week)}
												style={{ width: "auto", marginRight: "5px", display: "inline-block" }}
												onClick={() => toggleTaxableWeek({ week, employeeID })}
												type="checkbox"
												title={format(new Date(week), "do MMM yyyy")}
											/>
										))}

										{weeksBeforeTermination?.map((week) => (
											<input
												key={week}
												disabled={ready}
												checked={employee?.taxableWeeks?.includes(week)}
												style={{ width: "auto", marginRight: "5px", display: "inline-block" }}
												onClick={() => toggleTaxableWeek({ week, employeeID })}
												type="checkbox"
												title={format(new Date(week), "do MMM yyyy")}
											/>
										))}

										{!weeksBeforeTermination &&
											payPeriodDetails.taxableWeeks.map((week) => (
												<input
													key={week}
													disabled={ready}
													checked={employee.taxableWeeks?.includes?.(week)}
													style={{
														width: "auto",
														marginRight: "5px",
														display: "inline-block",
													}}
													onClick={() => toggleTaxableWeek({ week, employeeID })}
													type="checkbox"
													title={format(new Date(week), "do MMM yyyy")}
												/>
											))}

										{!weeksBeforeTermination &&
											weeksAfterPeriodEnd.map((week) => (
												<input
													key={week}
													disabled={ready}
													checked={employee.taxableWeeks?.includes?.(week)}
													style={{
														width: "auto",
														marginRight: "5px",
														display: "inline-block",
														accentColor: "red",
													}}
													onClick={() => toggleTaxableWeek({ week, employeeID })}
													type="checkbox"
													title={format(new Date(week), "do MMM yyyy")}
												/>
											))}
									</div>
									{jurisdiction === "MT" && (
										<small>Do not edit/click unless you know what you are doing.</small>
									)}
								</div>
								<PayslipNote
									ready={cannotEdit}
									note={employee?.note ?? payslip?.note}
									employeeID={employeeID}
								/>
							</div>
						</div>
					)}
					{userEntersData && (
						<div
							style={{
								gridColumnStart: "8",
								gridColumnEnd: "13",
								gridRowStart: "1",
								gridRowEnd: "3",
								lineHeight: "38px",
							}}
						>
							<PayslipNote
								ready={cannotEdit}
								note={employee?.note ?? payslip?.note}
								employeeID={employeeID}
							/>
							<PayslipNote
								ready={cannotEdit}
								note={employee?.note ?? payslip?.note}
								employeeID={employeeID}
								toAdvisor={true}
							/>
						</div>
					)}

					{!userEntersData && (
						<>
							<div
								style={{
									gridColumnStart: "8",
									gridColumnEnd: "13",
									gridRowStart: "1",
									gridRowEnd: "3",
									lineHeight: "38px",
								}}
							>
								<div
									style={{
										display: "grid",
										gridGap: "1.5rem",
										grid: "1.5rem",
										gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
									}}
								>
									<div style={{ gridColumn: "span 2 / span 6", lineHeight: "22px" }}>Tax Name</div>
									<div style={{ gridColumn: "span 2 / span 6", lineHeight: "22px" }}>Employee</div>
									<div style={{ gridColumn: "span 2 / span 6", lineHeight: "22px" }}>Employer</div>

									{taxesToDisplay?.map((tax) => (
										<TaxRow
											key={tax.taxName}
											tax={tax}
										/>
									))}
								</div>
								<TotalsBlock />
							</div>

							<div
								style={{
									gridColumnStart: "8",
									gridColumnEnd: "13",
									gridRowStart: "3",
									gridRowEnd: "3",
									lineHeight: "38px",
									height: "min-content",
								}}
							>
								<div
									style={{
										display: "grid",
										gridGap: "1rem 1.5rem",
										marginBottom: "1rem",
										grid: "1.5rem",
										gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
										lineHeight: "22px",
									}}
								>
									<div style={{ gridColumn: "span 6 / span 6", borderTop: "1px solid #bbbcbc" }}>
										<strong>Vacation Leave</strong>
									</div>

									<div style={{ gridColumn: "span 4 / span 6" }}>
										<strong>Entitled</strong>
									</div>
									<Tooltip
										content={
											<div
												style={{
													display: "grid",
													paddingLeft: "1rem",
													paddingRight: "1rem",
													gridGap: "1rem 1.5rem",
													gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
												}}
											>
												<div style={{ gridColumn: "span 1 / span 2", textAlign: "left" }}>
													<strong>Entitled</strong>
												</div>
												<div style={{ gridColumn: "span 1 / span 2", textAlign: "right" }}>
													{(payslip?.leaveBalance
														? proRataLeave
															? payslip?.leaveBalance?.entitledToDate
															: payslip?.leaveBalance?.entitled
														: (proRataLeave
																? leaveBalance?.entitledToDate
																: leaveBalance?.entitledThisYear) ||
														  0 + (leaveBalance?.manualAdjustment || 0)
													).toFixed(2)}
												</div>
												<div style={{ gridColumn: "span 1 / span 2", textAlign: "left" }}>
													<strong>Carried Over</strong>
												</div>
												<div style={{ gridColumn: "span 1 / span 2", textAlign: "right" }}>
													{(
														{
															...(payslip?.leaveBalance
																? payslip.leaveBalance
																: leaveBalance),
														}?.carryover || 0
													).toFixed(2)}
												</div>
												<div style={{ gridColumn: "span 1 / span 2", textAlign: "left" }}>
													<strong>TOIL</strong>
												</div>
												<div style={{ gridColumn: "span 1 / span 2", textAlign: "right" }}>
													{(
														{
															...(payslip?.leaveBalance
																? payslip.leaveBalance
																: leaveBalance),
														}?.toil || 0
													).toFixed(2)}
												</div>
											</div>
										}
									>
										<div style={{ gridColumn: "span 2 / span 6" }}>
											{(payslip?.leaveBalance
												? (proRataLeave
														? payslip?.leaveBalance?.entitledToDate
														: payslip?.leaveBalance?.entitled) +
												  payslip?.leaveBalance?.toil +
												  payslip?.leaveBalance?.carryover
												: (proRataLeave
														? leaveBalance?.entitledToDate
														: leaveBalance?.entitledThisYear) ||
												  0 +
														(leaveBalance?.manualAdjustment || 0) +
														(leaveBalance?.toil || 0) +
														(leaveBalance?.carryover || 0)
											).toFixed(2)}
										</div>
									</Tooltip>

									<div style={{ gridColumn: "span 4 / span 6" }}>
										<strong>Consumed</strong>
									</div>

									<div style={{ gridColumn: "span 2 / span 6" }}>
										{(payslip?.leaveBalance?.consumed ?? leaveBalance?.consumed)?.toFixed(2)}
									</div>

									<div style={{ gridColumn: "span 4 / span 6" }}>
										<strong>Balance to Date</strong>
									</div>

									<div style={{ gridColumn: "span 2 / span 6" }}>
										{(
											payslip?.leaveBalance?.remainingToDate ?? leaveBalance?.balanceToDate
										)?.toFixed(2)}
										{!ready &&
											(leaveBalance?.balanceToDate
												? leaveBalance?.balanceToDate
												: payslip?.leaveBalance?.remainingToDate) > 0 &&
											!hasPaidLeave && (
												<button
													style={{ marginLeft: "5px" }}
													className="btn btn-xs btn-default add-adjustment"
													type="button"
													title="Pay Leave"
													onClick={() => {
														const salary = salaries[0];

														const amount =
															payslip?.leaveBalance?.remainingToDate ??
															leaveBalance?.balanceToDate?.toFixed(2);

														switch (jurisdiction) {
															case "UK":
																payrollUpdateAllowance({
																	id: uuidv4(),
																	name: "Paid Holiday Balance",
																	salary: salary.id,
																	employeeID,
																	allowanceType: "6020", // paid leave type
																	method: "HolidayPay", // paid leave type
																	paidBalance: true,
																	input: parseFloat(amount).toFixed(2),
																	leaveHours: parseFloat(amount).toFixed(2),
																	isNew: true,
																});
																break;
															case "MT":
															default:
																payrollUpdateAllowance({
																	id: uuidv4(),
																	salary: salary.id,
																	employeeID,
																	allowanceType: 75668, // paid leave type
																	input: parseFloat(amount).toFixed(2),
																	hours: parseFloat(amount).toFixed(2),
																	amount:
																		parseFloat(amount).toFixed(2) *
																		salary.hourlySalaryAmount,
																	isNew: true,
																});
																break;
														}
													}}
												>
													<BanknotesIcon className="h-7 w-7 media-middle" />
												</button>
											)}
									</div>

									<div style={{ gridColumn: "span 4 / span 6" }}>
										<strong>Annual Balance</strong>
									</div>

									<div style={{ gridColumn: "span 2 / span 6" }}>
										{(payslip?.leaveBalance?.remaining ?? leaveBalance?.balanceThisYear)?.toFixed(
											2,
										)}
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => {
	const employee = state.payroll?.employees?.find?.((employee) => employee.id == ownProps.employeeID) || {};

	if (ownProps.employeeID == 630060) {
		console.log("ownProps.employeeID", ownProps.employeeID, employee);
	}

	const payPeriodDetails = state.payroll.payPeriodDetails;

	const leaveBalances = state?.leave?.leaveBalancesByType?.["6020"];

	const engagementDate = employee.engagementDate;
	const terminationDate = employee.terminationDate;

	const extraEngagementWeeks = [];
	let weeksBeforeTermination = null;

	const isCasual = employee?.salaries?.find((s) => s.casual == "yes");

	//  before
	if (payPeriodDetails.periodStart >= payPeriodDetails.previousOvertimeCutOff) {
		let monday = startOfWeek(new Date(payPeriodDetails.previousOvertimeCutOff), { weekStartsOn: 1 });

		let mondayString = format(monday, "yyyy-MM-dd");
		// console.log("extraEngagementWeeksmondayString", mondayString, payPeriodDetails.periodStart);

		while (mondayString < payPeriodDetails.periodStart) {
			extraEngagementWeeks.push(mondayString);

			monday = addWeeks(monday, 1);
			mondayString = format(monday, "yyyy-MM-dd");
		}
	}
	// console.log(
	// 	"extraEngagementWeeks",
	// 	extraEngagementWeeks,
	// 	payPeriodDetails.periodStart,
	// 	payPeriodDetails.previousOvertimeCutOff,
	// );

	if (engagementDate >= payPeriodDetails.periodStart) {
		let monday = startOfWeek(new Date(engagementDate), { weekStartsOn: 1 });

		let mondayString = format(monday, "yyyy-MM-dd");

		if (mondayString < payPeriodDetails.periodStart) {
			extraEngagementWeeks.push(mondayString);
		}
	}

	if (
		terminationDate &&
		terminationDate >= payPeriodDetails.periodStart &&
		terminationDate <= payPeriodDetails.periodEnd
	) {
		let monday = startOfWeek(new Date(payPeriodDetails.periodStart), { weekStartsOn: 1 });
		let mondayString = format(monday, "yyyy-MM-dd");

		let mondayOfEnagement = startOfWeek(new Date(engagementDate), { weekStartsOn: 1 });
		let mondayOfEnagementString = format(mondayOfEnagement, "yyyy-MM-dd");

		weeksBeforeTermination = [];

		while (mondayString <= terminationDate) {
			if (mondayString >= payPeriodDetails.periodStart && mondayString >= mondayOfEnagementString) {
				weeksBeforeTermination.push(mondayString);
			}

			monday = addWeeks(monday, 1);
			mondayString = format(monday, "yyyy-MM-dd");
		}
	}

	const weeksAfterPeriodEnd = employee.taxableWeeks?.filter((w) => w > payPeriodDetails.periodEnd);

	const salaryUpdatedAfterPayslip =
		employee?.payslip?.updatedAt && employee?.salaries?.find((s) => s.updatedAt > employee?.payslip?.updatedAt);

	const leaveBalance = leaveBalances?.find((item) => item.agreementId == ownProps.employeeID);

	const { isAdmin } = state.my.profile || {};

	const managedByAdvisor = !!state?.company?.settings?.advisor;
	const clientEntersPayrollData = state?.company?.settings?.clientEntersPayrollData;
	const userIsAdvisor = state?.my?.profile?.advisorId === state.company?.settings?.advisor || isAdmin;
	const userIsClient = !managedByAdvisor || !userIsAdvisor;
	const userEntersData = userIsClient && clientEntersPayrollData;

	return {
		// ...state.payroll,
		engagementDate,
		terminationDate,
		payPeriodDetails: state.payroll.payPeriodDetails,
		employee,
		extraEngagementWeeks,
		weeksBeforeTermination,
		weeksAfterPeriodEnd,
		salaryUpdatedAfterPayslip,
		currency: state?.company?.settings?.currency,
		leaveBalance,
		jurisdiction: state.company?.settings?.jurisdiction,
		proRataLeave: state?.leave?.leaveSettings?.proRataLeave || state?.leave?.leaveSettings?.projectedBalance,
		userEntersData,
		// generating: employee.generating,
		// error: employee.error
		// ...state.employees,
	};
};

const mapDispatchToProps = (dispatch) => ({
	overtimeGetList: () => dispatch(OVERTIME_ACTIONS.overtimeGetList()),
	allowanceGetList: () => dispatch(ALLOWANCE_ACTIONS.allowanceGetList()),
	payrollToggleEmployeeExpand: (employeeID) => dispatch(PAYROLL_ACTIONS.payrollToggleEmployeeExpand(employeeID)),
	payrollSaveAllowances: (employeeID, ready, saveOnly) =>
		dispatch(PAYROLL_ACTIONS.payrollSaveAllowances(employeeID, ready, saveOnly)),
	previewPayslip: (payslipID) => dispatch(PAYROLL_ACTIONS.previewPayslip(payslipID)),
	toggleTaxableWeek: (item) => dispatch(PAYROLL_ACTIONS.toggleTaxableWeek(item)),
	payrollUpdateAllowance: (item) => dispatch(PAYROLL_ACTIONS.payrollUpdateAllowance(item)),
	unprocessTimesheetsModal: (agreementID) => dispatch(PAYROLL_ACTIONS.unprocessTimesheetsModal(agreementID)),
	getSingularBalance: (agreementID, until) => dispatch(LEAVE_ACTIONS.getSingularBalance(agreementID, until)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeRow);
